function extractYear(rawTitle) {
  // Look for 4-digit years in various formats
  const yearPatterns = [
    // Basic 4-digit year. The group must capture the FULL year — an earlier
    // version captured only the (19|20) prefix, so `matches[..][1]` was "20",
    // failed the range check, and every space-separated "Title 2013 1080p"
    // name lost its year (dot-separated names only survived via the
    // `.2013.` pattern below).
    /\b((?:19|20)\d{2})\b/g,
    /\((\d{4})\)/g, // Year in parentheses
    /\[(\d{4})\]/g, // Year in brackets
    /\.(\d{4})\./g, // Year between dots
  ];

  for (const pattern of yearPatterns) {
    const matches = [...rawTitle.matchAll(pattern)];
    if (matches.length > 0) {
      // Return the last found year (often more accurate)
      const year = parseInt(
        matches[matches.length - 1][1] || matches[matches.length - 1][0],
      );
      if (year >= 1900 && year <= new Date().getFullYear() + 2) {
        return year;
      }
    }
  }

  return null;
}

// Metadata tokens that mark "everything from here on is release info, not
// title". Used to validate a year-boundary cut and as a resolution fallback.
const METADATA_AFTER_BOUNDARY =
  /\b(\d{3,4}p|4k|8k|uhd|blu[\s.-]?ray|bluray|remux|bdremux|web[\s.-]?(dl|rip)?|webrip|hdtv|bdrip|brrip|dvdrip|hevc|x26[45]|h26[45]|av1|xvid|truehd|atmos|dts|hdr10?|dovi|dolby[\s.]?vision|multi)\b/i;

function cleanBoundaryPrefix(prefix) {
  let p = String(prefix || "");
  p = p.replace(/[-–—:\s]+$/g, "");
  p = p.replace(/\s+/g, " ").trim();
  // TV names cut at the year/resolution still carry their SxxExx marker —
  // strip it so the prefix reads as the show title.
  p = p.replace(/\bS\d{1,2}(E\d{1,2})?\s*$/i, "").trim();
  p = p.replace(/\b\d{1,2}x\d{1,2}\s*$/i, "").trim();
  return p.length >= 2 ? p : null;
}

// Scene/release names put the TITLE before the release year and quality
// tokens ("Rush 2013 2160p UHD BluRay REMUX DV P7 HDR MULTi TrueHD Atmos 7 1
// H265 BEN THE MEN"). Token-by-token junk removal can't win against that —
// DV profiles ("P7"), spaced channel layouts ("7 1"), and multi-word groups
// ("BEN THE MEN") always leak through. Cutting at the boundary gets the
// clean title in one move:
//   1. The RELEASE year — the LAST year token followed by metadata (or by
//      nothing). Later-year preference keeps title years intact ("Blade
//      Runner 2049 2017 2160p…" cuts at 2017, not 2049).
//   2. Else the first resolution token ("Show S02E03 1080p WEB…").
// Returns null when no confident boundary exists (plain titles, bracketed
// year names) — callers fall back to the token-removal pipeline.
function extractBoundaryTitle(rawTitle) {
  let s = String(rawTitle || "").replace(/\.[a-z0-9]{2,4}$/i, "");
  s = s.replace(/[._]/g, " ");
  s = s.replace(/[[(].*?[\])]/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  if (!s) return null;

  const yearTokens = [...s.matchAll(/\b(?:19|20)\d{2}\b/g)];
  for (let i = yearTokens.length - 1; i >= 0; i--) {
    const m = yearTokens[i];
    const after = s.slice(m.index + m[0].length).trim();
    if (after === "" || METADATA_AFTER_BOUNDARY.test(after)) {
      const prefix = cleanBoundaryPrefix(s.slice(0, m.index));
      if (prefix) return prefix;
      // Year sits at the head — it IS the title ("1917 2019 1080p…").
      // Fall through to the resolution boundary below.
      break;
    }
  }

  const res = s.match(/\b(\d{3,4}p|4k|8k)\b/i);
  if (res) {
    const prefix = cleanBoundaryPrefix(s.slice(0, res.index));
    if (prefix) return prefix;
  }
  return null;
}

function detectMediaType(rawTitle) {
  const tvIndicators = [
    /\bS\d{1,2}E\d{1,2}\b/i, // S01E01 format
    /\bS\d{1,2}\b/i, // Season indicators
    /\bSeason\s+\d+/i, // "Season 1"
    /\bEpisode\s+\d+/i, // "Episode 1"
    /\bEp\s*\d+/i, // "Ep 1"
    /\b\d{1,2}x\d{1,2}\b/i, // 1x01 format
  ];

  return tvIndicators.some((pattern) => pattern.test(rawTitle))
    ? "tv"
    : "movie";
}

function getJunkTokens(level = "conservative") {
  // Base tokens that are almost always safe to remove
  const baseTokens = [
    // Quality indicators
    "1080p",
    "720p",
    "2160p",
    "4k",
    "uhd",
    "8k",
    "480p",
    "576p",
    "hdr",
    "hdr10",
    "hdr10\\+",
    "dolby\\s*vision",
    "dv",
    "dovi",

    // Source indicators
    "web",
    "webrip",
    "web-dl",
    "webdl",
    "bluray",
    "blu-ray",
    "brrip",
    "bdrip",
    "dvdrip",
    "dvd",
    "hdtv",
    "pdtv",
    "cam",
    "ts",
    "tc",
    "scr",
    "screener",
    "r5",
    "dvdscr",
    "workprint",
    "ppv",
    "vod",

    // Codecs and formats
    "x265",
    "h265",
    "x264",
    "h264",
    "hevc",
    "avc",
    "xvid",
    "divx",
    "av1",
    "vp9",
    "mpeg",
    "wmv",

    // Audio
    "aac",
    "ac3",
    "dts",
    "ddp5",
    "dd5",
    "dd5\\.1",
    "truehd",
    "atmos",
    "flac",
    "mp3",
    "ogg",
    "opus",
    "5\\.1",
    "7\\.1",
    "2\\.0",

    // Bit depth and encoding
    "10bit",
    "10-bit",
    "8bit",
    "8-bit",
    "hq",

    // Release groups and sites
    "yify",
    "yts",
    "rarbg",
    "etrg",
    "sparks",
    "ganool",
    "shaanig",
    "psarips",
    "pahe",
    "rmteam",
    "torrentgalaxy",
    "tgx",
    "galaxyrg",
    "tigole",
    "qxr",
    "utm",
    "joy",
    "vxt",

    // Subtitles
    "sub",
    "subs",
    "subtitle",
    "subtitles",
    "esub",
    "esubs",
    "hardsub",
    "softsub",
    "cc",

    // Misc
    "repack",
    "proper",
    "real",
    "retail",
    "uncut",
    "extended",
    "directors?\\s*cut",
    "theatrical",
    "imax",
    "remastered",
    "complete",
    "full",
    "limited",
    "festival",
    "nf",
    "netflix",
    "amzn",
    "amazon",
    "hulu",
    "hbo",
    "max",
    "disney",
    "apple",
    "internal",
    "readnfo",
    "sample",
    "proof",

    // File indicators
    "avi",
    "mkv",
    "mp4",
    "mov",
    "wmv",
    "flv",
    "m4v",
    "3gp",

    // Size indicators
    "\\d+mb",
    "\\d+gb",
    "\\d+\\.\\d+gb",
  ];

  // More aggressive tokens that might be part of titles
  const aggressiveTokens = [
    // Languages and regions - these can be part of movie titles!
    "multi",
    "dual",
    "eng",
    "english",
    "kor",
    "korean",
    "ita",
    "italian",
    "fre",
    "french",
    "ger",
    "german",
    "spa",
    "spanish",
    "chi",
    "chinese",
    "jap",
    "japanese",
    "hindi",
    "rus",
    "russian",
    "por",
    "portuguese",
  ];

  if (level === "conservative") {
    return baseTokens;
  } else if (level === "aggressive") {
    return [...baseTokens, ...aggressiveTokens];
  }

  return baseTokens;
}

function cleanTitleWithLevel(rawTitle, level = "conservative") {
  if (!rawTitle) return "";

  let title = rawTitle.replace(/\.[a-z0-9]{2,4}$/i, ""); // remove extension

  // Strip release-group suffix BEFORE normalizing separators. The convention
  // is `Title.QualityTokens-RELEASEGROUP` (e.g. `...h264-EDITH`,
  // `...x265-YTS`). Once we replace `-` with spaces below, the suffix looks
  // like an ordinary trailing word and survives the rest of the pipeline.
  // Also handle a trailing parenthesized translation like
  // `...-SUPPLY (Les Simpson)` by peeling that off first.
  title = title.replace(/\s*[\[\(][^\[\(\]\)]*[\]\)]\s*$/, "");
  // Match the release-group suffix: starts with an uppercase letter or
  // digit, then any letters/digits. Catches `-EDITH`, `-FLUX`, `-FW`, `-YTS`,
  // and mixed-case names like `-PopHD` or `-RARBG`. Lowercase-only suffixes
  // are kept (they're often real word fragments, not release groups).
  title = title.replace(/[-–—][A-Z][A-Za-z0-9]+\s*$/, "");

  title = title.replace(/[\._\-]/g, " "); // replace separators with spaces
  title = title.replace(/[\[\(].*?[\]\)]/g, ""); // remove bracketed tags
  title = title.replace(/\{tmdb.*?\}/gi, ""); // remove {tmdb ...} tags

  // Remove years
  title = title.replace(/\b(19|20)\d{2}\b/g, "");

  // Remove season/episode indicators
  title = title.replace(/\bS\d{1,2}(E\d{1,2})?\b/gi, "");
  title = title.replace(/\b\d{1,2}x\d{1,2}\b/gi, "");
  title = title.replace(/\bSeason\s+\d+/gi, "");
  title = title.replace(/\bEpisode\s+\d+/gi, "");
  title = title.replace(/\bEp\s*\d+/gi, "");

  // Remove filesize indicators
  title = title.replace(/\b\d+(\.\d+)?\s*(MB|GB|TB)\b/gi, "");

  // Get appropriate junk tokens for this level
  const junkTokens = getJunkTokens(level);

  // Create pattern and remove junk tokens
  const pattern = new RegExp(`\\b(${junkTokens.join("|")})\\b`, "gi");
  title = title.replace(pattern, "");

  // Remove common patterns that might be missed
  title = title.replace(/\b\d{3,4}p\b/gi, ""); // Any resolution like 1080p
  title = title.replace(/\b\d+bit\b/gi, ""); // Any bit depth
  title = title.replace(/\bx\d{3}\b/gi, ""); // Codec indicators like x264
  title = title.replace(/\bh\d{3}\b/gi, ""); // Codec indicators like h264

  // Remove more specific patterns
  title = title.replace(/\b(aac|ac)\d+(\.\d+)?\b/gi, ""); // AAC5.1, AC3, etc.
  title = title.replace(/\b(dd|ddp)\d+(\.\d+)?\b/gi, ""); // DD5.1, DDP5.1, etc.
  title = title.replace(/\b(ma|hd)\s*\d+(\.\d+)?\b/gi, ""); // MA.7.1, HD.MA, etc.
  title = title.replace(/\b\d+(\.\d+)?\s*ch\b/gi, ""); // 5.1ch, 7.1ch, etc.

  // Remove release group patterns (more comprehensive)
  title = title.replace(/[-–—]\s*[A-Z0-9]+$/i, ""); // -YIFY, -RARBG, etc.
  title = title.replace(/\s+by\s+\w+$/i, "");
  title = title.replace(/\s+\w*rip$/i, "");

  // For conservative mode, be more careful with single letters and short words
  if (level === "conservative") {
    title = title.replace(/\b\d\b/g, ""); // Remove standalone digits
    // Only remove single letters if they're clearly not part of the title
    title = title.replace(/\b[a-z]\b(?!\s+[A-Z])/gi, ""); // Single letters not followed by capitalized word
  } else {
    // Aggressive mode - remove more aggressively
    title = title.replace(/\b\d\b/g, "");
    title = title.replace(/\b[a-z]\b/gi, ""); // Single letters
    title = title.replace(/\b(us|uk|ca)\b/gi, ""); // Country codes
  }

  // Clean up multiple separators and whitespace
  title = title.replace(/[_\-\.]+/g, " ");
  title = title.replace(/\s+/g, " ").trim();

  // Remove leading/trailing non-alphanumeric characters
  title = title.replace(/^[^\w]+|[^\w]+$/g, "");

  // Final cleanup - remove common leftover artifacts
  title = title.replace(/\b(chapter|part)\s*\d*\b/gi, "");

  return title;
}

function cleanMediaTitle(rawTitle, level = "conservative") {
  if (!rawTitle)
    return { title: "", year: null, mediaType: "movie", variants: [] };

  // Extract year and media type before cleaning
  const year = extractYear(rawTitle);
  const mediaType = detectMediaType(rawTitle);

  // Boundary cut first: title = everything before the release year /
  // resolution token. Beats token-removal on names whose junk isn't
  // enumerable (DV profiles, spaced channel layouts, multi-word release
  // groups like "BEN THE MEN"). Falls back to the token pipeline when no
  // confident boundary exists.
  const boundaryTitle = extractBoundaryTitle(rawTitle);

  // Try conservative cleaning first
  let title = boundaryTitle || cleanTitleWithLevel(rawTitle, level);

  // If title becomes too short or empty, try more aggressive cleaning
  if (title.length < 3 && level === "conservative") {
    title = cleanTitleWithLevel(rawTitle, "aggressive");
  }

  // If still too short, use minimal cleaning
  if (title.length < 3) {
    title = rawTitle.replace(/\.[a-z0-9]{2,4}$/i, "");
    title = title.replace(/[\._\-]/g, " ");
    title = title.replace(/\s+/g, " ").trim();
  }

  // Additional cleanup for very long titles (likely over-processed)
  if (title.length > 100) {
    const words = title.split(" ").filter((word) => word.length > 2);
    if (words.length > 0) {
      title = words.slice(0, 6).join(" ");
    }
  }

  // Generate variants for fallback searches
  const variants = [];

  // Add the primary cleaned title
  if (title && title.length >= 3) {
    variants.push(title);
  }

  // When the boundary cut won, keep the token-pipeline clean as a fallback
  // variant so callers that walk `variants` (e.g. the TMDB backdrop lookup)
  // still get the old behavior as a second guess.
  if (boundaryTitle) {
    const pipelineTitle = cleanTitleWithLevel(rawTitle, level);
    if (
      pipelineTitle &&
      pipelineTitle.length >= 3 &&
      !variants.includes(pipelineTitle)
    ) {
      variants.push(pipelineTitle);
    }
  }

  // Add more aggressive cleaning if we used conservative
  if (level === "conservative") {
    const aggressiveTitle = cleanTitleWithLevel(rawTitle, "aggressive");
    if (
      aggressiveTitle &&
      aggressiveTitle.length >= 3 &&
      !variants.includes(aggressiveTitle)
    ) {
      variants.push(aggressiveTitle);
    }
  }

  // Add minimal cleaning as final fallback
  const minimalTitle = rawTitle
    .replace(/\.[a-z0-9]{2,4}$/i, "")
    .replace(/[\._\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (
    minimalTitle &&
    minimalTitle.length >= 3 &&
    !variants.includes(minimalTitle)
  ) {
    variants.push(minimalTitle);
  }

  return {
    title: title || rawTitle,
    year,
    mediaType,
    variants: variants.length > 0 ? variants : [rawTitle],
  };
}

export default cleanMediaTitle;
